import { FC } from "react";
import Spacer, { ISpacer } from "apps/website/components/layout/Spacer/Spacer";

import { signupSteps } from "./constants/signupSteps";
import TextBody from "apps/website/components/base/Text/TextBody/TextBody";
import { Theme, themeRootClassMap } from "apps/website/maps/Theme.map";
import cx from "apps/website/utils/misc/cx";

export interface IStep {
  label: string;
  slugs: string[]; // all slugs that belong to this step
}

interface IStyleOptions {
  stepBgColor: Theme
  spacer?: {
    top?: ISpacer["size"]
    bottom?: ISpacer["size"],
  }
}

export interface ISimpleProgressStepsProps {
  steps: IStep[];
  currentSlug: string;
  hideLabels?: boolean; // hides the label text for each step
  styleOptions?: IStyleOptions
  // onStepClick?: boolean;
}

export const SimpleProgressSteps: FC<ISimpleProgressStepsProps> = ({
  steps = signupSteps,
  currentSlug,
  hideLabels = false,
  styleOptions,
}) => {
  // Find the index of the current step based on the slug
  const currentStepIndex = steps.findIndex((step) => step.slugs.includes(currentSlug));

  // Calculate progress percentage for each step
  const getProgress = (stepIndex: number): "0%" | "100%" => (stepIndex <= currentStepIndex ? "100%" : "0%");

  // Add handleStepClick function if onStepClick is true and we decide to add this functionality (see ticket)

  return (
    <>
      <Spacer size={styleOptions?.spacer?.top ?? "md-lg"} />
      <nav aria-label="Signup progress">
        <ol className="flex w-full gap-2 md:max-w-xl mx-auto">
          { steps.map((step, index) => {
            const isCurrent = index === currentStepIndex;
            return (
              <li
                key={`progress-${step.label}`}
                className="flex flex-1 flex-col gap-1 text-center"
                aria-current={isCurrent ? "step" : undefined}
              >
                { !hideLabels && (
                  <TextBody tag="span" size={5} align="center" className="uppercase">
                    { step.label }
                  </TextBody>
                ) }
                <div
                  className={cx("h-2 w-full rounded-full",
                    themeRootClassMap[styleOptions?.stepBgColor || "light-grey"])}
                  role="progressbar"
                  aria-valuenow={index <= currentStepIndex ? 100 : 0}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`Step ${index + 1} of ${steps.length}${isCurrent ? ", current step" : ""}`}
                >
                  <div
                    className={"h-full transition-all ease-in-out duration-300 rounded-full bg-brand"}
                    style={{ width: getProgress(index) }}
                    data-testid="progress-step"
                  />
                </div>
              </li>
            );
          }) }
        </ol>
      </nav>
      <Spacer size={styleOptions?.spacer?.bottom ?? "md-lg"} />
    </>
  );
};
